import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { firestore } from "../firebase";

const ContractList = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Get Role and Tenant from Session (faster than waiting for Auth check)
  const userRole = sessionStorage.getItem("userRole");
  const tenantId = sessionStorage.getItem("tenantId");
  const userEmail = sessionStorage.getItem("userEmail") || "";
  const userDisplayName = sessionStorage.getItem("userDisplayName") || "";

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        setLoading(true);
        setError("");

        // 1. Define the Base Reference
        const contractsRef = collection(firestore, "ContractMaster");
        let q;

        // 2. Build Query based on Role
        if (userRole === "superadmin") {
          // Superadmin sees EVERYTHING (No tenant filter)
          console.log("Fetching ALL contracts (Superadmin mode)");
          q = query(contractsRef, orderBy("createdAt", "desc"));
        } else if (tenantId) {
          // Tenant users see their tenant's contracts
          console.log(`Fetching contracts for Tenant ID: ${tenantId}`);
          q = query(
            contractsRef,
            where("tenantId", "==", tenantId),
            orderBy("createdAt", "desc")
          );
        } else {
          // If no tenant ID and not superadmin, show nothing (Safety fallback)
          console.warn("No Tenant ID found. Cannot fetch contracts.");
          setContracts([]);
          setLoading(false);
          return;
        }

        // 3. Execute Query
        const querySnapshot = await getDocs(q);

        // 4. Map Data
        const contractsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (userRole === "customer") {
          const normalizedEmail = userEmail.toLowerCase();
          const normalizedDisplayName = userDisplayName.toLowerCase();
          const customerContracts = contractsData.filter((contract) => {
            const offered = (contract.partyOffered || "").toLowerCase();
            const accepted = (contract.partyAccepted || "").toLowerCase();

            return (
              (normalizedEmail && (offered.includes(normalizedEmail) || accepted.includes(normalizedEmail))) ||
              (normalizedDisplayName && (offered.includes(normalizedDisplayName) || accepted.includes(normalizedDisplayName)))
            );
          });

          setContracts(customerContracts);
        } else {
          setContracts(contractsData);
        }
      } catch (err) {
        console.error("Error fetching contracts:", err);
        // Special handling for "Missing Index" error in Firestore
        if (err.message.includes("requires an index")) {
          setError(
            "System Error: A database index is missing. Please ask the developer to check the console and create the index link."
          );
        } else {
          setError("Failed to load contracts. " + err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [userRole, tenantId, userEmail, userDisplayName]);

  // Helper to style status chips
  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Draft":
        return "warning";
      case "Expired":
        return "error";
      case "Cancelled":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          Contract Management
        </Typography>
        {['admin', 'user'].includes(userRole) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/contracts/new")}
          >
            Create Contract
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : contracts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            No contracts found.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {userRole === "superadmin"
              ? "There are no contracts in the entire system."
              : "You haven't created any contracts for your organization yet."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>
                  <strong>Title</strong>
                </TableCell>
                <TableCell>
                  <strong>Parties</strong>
                </TableCell>
                <TableCell>
                  <strong>Dates</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow key={contract.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {contract.contractTitle || "Untitled"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ID: {contract.contractId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contract.partyOffered} &rarr; {contract.partyAccepted}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contract.startDate
                        ? new Date(
                            contract.startDate.seconds * 1000
                          ).toLocaleDateString()
                        : "N/A"}
                      {" - "}
                      {contract.endDate
                        ? new Date(
                            contract.endDate.seconds * 1000
                          ).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contract.status}
                      color={getStatusColor(contract.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => navigate(`/contracts/edit/${contract.id}`)}
                      title="Edit / View"
                    >
                      {contract.isFrozen ? <ViewIcon /> : <EditIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ContractList;
